/** @jsxImportSource @emotion/react */

import { useMemo } from 'react';

import { css } from '@emotion/react';
import { responsive, useTheme } from '@wci-tech/core-ui';

import { getContainerStyles } from '@/utils';

export const useStyles = (isDark: boolean) => {
  const { theme } = useTheme();

  return useMemo(
    () => ({
      wrapper: css`
        position: relative;
        width: 100%;
        color: var(--text-color);
        background-color: var(--common-color);

        overflow-anchor: none;

        ${responsive.create({
          minHeight: {
            default: '50vh',
            md: '70vh',
            lg: '82vh',
          },
        })}
      `,

      lottieWrapper: css`
        position: relative;
        width: 100%;
        overflow-anchor: none;

        ${responsive.create({
          minHeight: {
            default: '50vh',
            md: '70vh',
            lg: '82vh',
          },
        })}
      `,

      container: css`
        ${getContainerStyles(theme)};

        position: relative;
        display: flex;
        flex-direction: column;
        width: 100%;

        overflow-anchor: none;

        ${responsive.create({
          minHeight: {
            default: '50vh',
            md: '70vh',
            lg: '82vh',
          },
          paddingTop: {
            default: '20px',
            md: '45px',
            lg: '64px',
          },
          paddingBottom: {
            default: '20px',
            md: '40px',
          },
        })}
      `,

      breadcrumbWrapper: css`
        position: absolute;
        width: 100%;
        z-index: 2;
        display: flex;
        justify-content: flex-start;
        align-items: center;

        ${responsive.create({
          left: {
            default: '16px',
            md: '24px',
            lg: '64px',
          },
          top: {
            default: '8px',
            md: '16px',
            lg: '16px',
          },
        })}
      `,

      contentWrapper: css`
        width: 100%;
        max-width: 900px;
        margin-left: auto;
        margin-right: auto;
        z-index: 2;
        padding: 0 ${theme.spacing.get(16)};

        ${responsive.create({
          marginBottom: {
            default: '-80px',
            md: '65px',
          },
        })}
      `,

      headerSection: css`
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
        text-align: center;
      `,

      title: css`
        color: var(--text-color);
        font-weight: 600;
        line-height: 1.2;
        text-align: center;
        transition: color 0.3s ease;

        ${responsive.create({
          fontSize: {
            default: '30px',
            md: '36px',
            lg: '48px',
          },
        })}
      `,

      description: css`
        color: var(--text-color);
        max-width: 800px;
        text-align: center;
        transition: color 0.3s ease;

        ${responsive.create({
          fontSize: {
            default: '14px',
            md: '18px',
            lg: '24px',
          },
        })}
      `,

      bookDemoBtn: css`
        border: none;
        padding: 12px 32px;
        font-weight: 600;

        ${responsive.create({
          width: {
            default: '100%',
            md: 'auto',
          },
        })}

        --btn-text-hover-color: #f5f6fa;
      `,

      lottieMedia: css`
        position: relative;
        z-index: 1;
        width: 100%;
        height: 100%;
        display: block;

        transform-origin: center center;
        will-change: transform;

        overflow-anchor: none;

        ${responsive.create({
          transform: {
            default: 'scale(1.08)',
            md: 'scale(1.05)',
            lg: 'scale(1)',
          },
        })}

        canvas,
        svg,
        img {
          width: 100% !important;
          height: 100% !important;
          display: block;

          overflow-anchor: none;

          transition: ${
            theme.transitions.createTransition({
              property: 'opacity',
              duration: '0.3s',
              easing: 'ease',
            }).styles
          };
        }
      `,

      lottieLayer: css`
        position: absolute;
        inset: 0;
        z-index: 0;

        width: 100%;
        height: 100%;

        padding: ${theme.spacing.get(20)} ${theme.spacing.get(28)} ${theme.spacing.get(20)}
          ${theme.spacing.get(28)};

        pointer-events: none;
        overflow: hidden;

        /*
         * The animation/image replacement must never become
         * Chrome's scroll anchor.
         */
        overflow-anchor: none;

        &::before,
        &::after {
          content: '';
          position: absolute;
          top: 0;
          bottom: 0;
          z-index: 2;
          pointer-events: none;

          ${responsive.create({
            display: {
              default: 'block',
              md: 'none',
              lg: isDark ? 'none' : 'block',
            },
            width: {
              default: '110px',
              md: '0px',
              lg: isDark ? '0px' : '90px',
            },
          })}
        }

        &::before {
          left: 0;

          background: linear-gradient(
            to right,
            var(--common-color) 0%,
            var(--common-color) 45%,
            transparent 100%
          );
        }

        &::after {
          right: 0;

          background: linear-gradient(
            to left,
            var(--common-color) 0%,
            var(--common-color) 45%,
            transparent 100%
          );
        }
      `,

      viewDocBtn: css`
        &:hover {
          background-color: ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)'};
          border-color: ${isDark ? '#fff' : 'currentColor'};
        }

        ${responsive.create({
          width: {
            default: '100%',
            md: 'auto',
          },
        })}
      `,

      buttonSection: css`
        width: 100%;
      `,

      contentArea: css`
        position: relative;
        z-index: 1;
        width: 100%;
        flex: 1;

        overflow-anchor: none;
      `,

      btnIcon: css`
        svg {
          stroke-width: 0.6;
        }
      `,
    }),
    [theme, isDark],
  );
};
